import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Activity, User, ShoppingCart, Package, Heart, Car, LogOut, Eye, Filter, Clock, ArrowRight, Search, X, ChevronRight, User as UserIcon } from 'lucide-react'

export default function AdminActivityPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [activities, setActivities] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [filterRole, setFilterRole] = useState('all')
  const [filterAction, setFilterAction] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [view, setView] = useState<'timeline' | 'users'>('users')
  const [filteredUsers, setFilteredUsers] = useState<any[]>([])

  useEffect(() => {
    if (!profile || profile.role !== 'admin') return

    loadUsers()
  }, [profile])

  useEffect(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase()
      const filtered = users.filter(user =>
        user.email?.toLowerCase().includes(lowerQuery) ||
        user.full_name?.toLowerCase().includes(lowerQuery) ||
        user.phone?.toLowerCase().includes(lowerQuery)
      )
      setFilteredUsers(filtered)
    } else {
      setFilteredUsers(users)
    }
  }, [searchQuery, users])

  useEffect(() => {
    if (selectedUser) {
      loadUserActivities(selectedUser.id)
    }
  }, [selectedUser, filterAction])

  const loadUsers = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })
    setUsers(data || [])
    setLoading(false)
  }

  const loadUserActivities = async (userId: string) => {
    setLoading(true)
    let query = supabase
      .from('activity_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100)

    if (filterAction !== 'all') {
      query = query.eq('action', filterAction)
    }

    const { data } = await query
    setActivities(data || [])
    setLoading(false)
  }

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'login': return <LogOut size={16} />
      case 'logout': return <LogOut size={16} />
      case 'page_view': return <Eye size={16} />
      case 'order_placed': return <ShoppingCart size={16} />
      case 'cart_updated': return <ShoppingCart size={16} />
      case 'wishlist_updated': return <Heart size={16} />
      case 'vehicle_registered': return <Car size={16} />
      case 'tow_request_created': return <Car size={16} />
      case 'claim_filed': return <Activity size={16} />
      default: return <Activity size={16} />
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'login': return 'var(--green)'
      case 'logout': return 'var(--red)'
      case 'order_placed': return 'var(--blue)'
      case 'cart_updated': return 'var(--amber)'
      default: return 'var(--muted)'
    }
  }

  if (!profile || profile.role !== 'admin') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
        <h2>Access Denied</h2>
        <p>Admin access required</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Activity Monitor
          </h1>
          <p style={{ color: 'var(--muted)' }}>
            Track user sessions and activities
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
            Back to Dashboard
          </button>
          <button
            onClick={() => loadUsers()}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--red)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Clock size={16} />
            Refresh
          </button>
          {selectedUser && (
            <button
              onClick={() => {
                setSelectedUser(null)
                setView('users')
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Back to Users
            </button>
          )}
        </div>
      </div>

      {/* Users Table View */}
      {view === 'users' && (
        <>
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Search size={16} style={{ color: 'var(--muted)' }} />
            <input
              type="text"
              placeholder="Search by email, name, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'white',
                fontSize: '0.875rem'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--muted)'
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              <UserIcon size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>No users found</p>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--bg)' }}>
                  <tr>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>User</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>Role</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>Phone</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem', color: 'var(--muted)' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'var(--red-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--red)',
                            fontWeight: 700
                          }}>
                            {user.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{user.full_name || 'Unknown'}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>ID: {user.id.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          background: 'var(--bg)',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          color: 'var(--muted)'
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text)' }}>{user.email || 'N/A'}</td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text)' }}>{user.phone || 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          background: user.is_online ? 'var(--green-light)' : 'var(--muted)',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: user.is_online ? 'var(--green)' : 'var(--muted)'
                        }}>
                          {user.is_online ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setView('timeline')
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--red)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          View Activity
                          <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Activity Timeline View */}
      {view === 'timeline' && selectedUser && (
        <>
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'var(--red-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--red)',
                fontWeight: 700,
                fontSize: '1.5rem'
              }}>
                {selectedUser.full_name?.charAt(0) || selectedUser.email?.charAt(0) || 'U'}
              </div>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                  {selectedUser.full_name || 'Unknown User'}
                </h2>
                <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                  {selectedUser.email} • {selectedUser.role} • {selectedUser.phone || 'No phone'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Actions</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="page_view">Page Views</option>
              <option value="order_placed">Orders</option>
              <option value="cart_updated">Cart Updates</option>
              <option value="wishlist_updated">Wishlist</option>
              <option value="tow_request_created">Towing Requests</option>
            </select>
            <button
              onClick={() => loadUserActivities(selectedUser.id)}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--red)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              Loading activities...
            </div>
          ) : activities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>No activities found for this user</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  style={{
                    padding: '1.25rem',
                    background: 'white',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: getActionColor(activity.action) + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: getActionColor(activity.action)
                  }}>
                    {getActivityIcon(activity.action)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                      {activity.action.replace('_', ' ').toUpperCase()}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                      {activity.page && `→ ${activity.page}`}
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <>
                          <span style={{ margin: '0 0.5rem' }}>|</span>
                          <span>
                            {Object.entries(activity.details).map(([key, value]) => (
                              <span key={key} style={{ marginLeft: '0.5rem' }}>
                                {key}: {String(value)}
                              </span>
                            ))}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: '150px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
                      <Clock size={12} />
                      {new Date(activity.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
